import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, AlertTriangle, CheckCircle, X, Edit, Ban } from 'lucide-react';
import PageSkeleton from '../common/Skeleton';
import ProductAutocomplete from './ProductAutocomplete';
import { 
  Button, 
  IconButton,
  DataTable, 
  TableHeader, 
  TableHeaderCell, 
  TableBody, 
  TableRow, 
  TableCell, 
  EmptyState, 
  AlertMessage, 
  PageHeader,
  SearchFilterBar,
  Modal
} from '../common/ui';
import { B2BProduct, LocalProduct, ProductStatus, ProductFilter } from '../../types/orders';
import { fetchB2BProducts, fetchLocalProducts, updateProductMapping, deleteProduct } from '../../services/b2bSyncApi';
import { useSyncStatus } from '../../hooks/useSyncStatus';
import { SyncProgressBar } from './SyncProgressBar';
import { SyncStatusIndicator } from './SyncStatusIndicator';

const OrderSettings: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<B2BProduct[]>([]);
  const [localProducts, setLocalProducts] = useState<LocalProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState<ProductFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [bulkDeleteModal, setBulkDeleteModal] = useState<{ show: boolean; count: number }>({ show: false, count: 0 });
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  const [unlinkConfirmModal, setUnlinkConfirmModal] = useState<{ show: boolean; product: B2BProduct | null }>({ show: false, product: null });
  const [linkModal, setLinkModal] = useState<{ show: boolean; product: B2BProduct | null }>({ show: false, product: null });
  const isTestEnv = typeof process !== 'undefined' && (process as any).env && (process as any).env.NODE_ENV === 'test';
  let isDesktop = true;
  if (!isTestEnv) {
    if (typeof window !== 'undefined' && typeof (window as any).matchMedia === 'function') {
      const mql = (window as any).matchMedia('(min-width: 768px)');
      isDesktop = !!(mql && mql.matches);
    }
  }
  
  const { syncStatus, syncingProducts, syncProducts: syncProductsApi } = useSyncStatus();

  useEffect(() => {
    fetchData();
  }, [filter]);

  // Clear selections when filter or search changes
  useEffect(() => {
    setSelectedProductIds(new Set());
    setLastSelectedIndex(null);
  }, [filter, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch B2B products
      const productsRes = await fetchB2BProducts(filter);
      setProducts(productsRes.products || []);

      // Fetch local products for dropdown
      const localRes = await fetchLocalProducts();
      setLocalProducts(localRes.products || []);

      setLoading(false);
    } catch (err: any) {
      console.error('OrderSettings: Error fetching data:', err);
      setError(`Failed to fetch product data: ${err.response?.data?.error || err.message}`);
      setLoading(false);
    }
  };

  const handleSyncProducts = async () => {
    try {
      setError('');
      setSuccess('');

      const result = await syncProductsApi();
      setSuccess(
        `Sync completed: ${result.synced} products synced, ${result.unmapped} unmapped`
      );
      
      // Refresh data after sync
      await fetchData();
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      console.error('Error syncing products:', err);
      setError(err.response?.data?.message || 'Failed to sync products');
    }
  };

  const handleLinkProduct = async (b2bProduct: B2BProduct, localProduct: LocalProduct) => {
    try {
      await updateProductMapping(b2bProduct.id, {
        local_product_id: localProduct.id,
        is_ignored: false
      });

      // Update local state
      setProducts(products.map(p => 
        p.id === b2bProduct.id 
          ? { 
              ...p, 
              local_product_id: localProduct.id,
              local_product_name: localProduct.name,
              mapping_manual: true,
              is_ignored: false
            }
          : p
      ));

      setSuccess(`Linked "${b2bProduct.name}" to "${localProduct.name}"`);
      setLinkModal({ show: false, product: null });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error linking product:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to link product');
    }
  };

  const handleMarkAsIgnored = async (product: B2BProduct) => {
    try {
      await updateProductMapping(product.id, {
        local_product_id: null,
        is_ignored: true
      });

      // Update local state
      setProducts(products.map(p => 
        p.id === product.id 
          ? { 
              ...p, 
              local_product_id: null,
              local_product_name: null,
              is_ignored: true
            }
          : p
      ));

      setSuccess(`Marked "${product.name}" as ignored`);
      setLinkModal({ show: false, product: null });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error marking product as ignored:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to mark as ignored');
    }
  };

  const handleUnlinkProduct = async (product: B2BProduct) => {
    try {
      await updateProductMapping(product.id, {
        local_product_id: null,
        is_ignored: false
      });

      // Update local state
      setProducts(products.map(p => 
        p.id === product.id 
          ? { 
              ...p, 
              local_product_id: null,
              local_product_name: null,
              mapping_manual: false,
              is_ignored: false
            }
          : p
      ));

      setSuccess(`Unlinked "${product.name}"`);
      setUnlinkConfirmModal({ show: false, product: null });
      setLinkModal({ show: false, product: null });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error unlinking product:', err);
      setError(err.response?.data?.error || 'Failed to unlink product');
      setUnlinkConfirmModal({ show: false, product: null });
    }
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredProducts.map(p => p.id));
      setSelectedProductIds(allIds);
    } else {
      setSelectedProductIds(new Set());
    }
    setLastSelectedIndex(null);
  };

  const handleProductSelect = (productId: number, index: number, e: React.SyntheticEvent) => {
    e.stopPropagation();
    const newSelection = new Set(selectedProductIds);
    const nativeEvent = e.nativeEvent as MouseEvent;

    if (nativeEvent.shiftKey && lastSelectedIndex !== null) {
      // Range selection with Shift+Click
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const rangeProducts = filteredProducts.slice(start, end + 1);
      rangeProducts.forEach(p => newSelection.add(p.id));
    } else if (nativeEvent.ctrlKey || nativeEvent.metaKey) {
      // Toggle individual selection with Ctrl/Cmd+Click
      if (newSelection.has(productId)) {
        newSelection.delete(productId);
      } else {
        newSelection.add(productId);
      }
    } else {
      // Simple click - toggle single item
      if (newSelection.has(productId)) {
        newSelection.delete(productId);
      } else {
        newSelection.add(productId);
      }
    }

    setSelectedProductIds(newSelection);
    setLastSelectedIndex(index);
  };

  // Bulk action handlers
  const handleBulkDelete = async () => {
    if (selectedProductIds.size === 0) return;

    setBulkOperationLoading(true);
    setError('');
    setSuccess('');

    const idsToDelete = Array.from(selectedProductIds);
    let successCount = 0;
    let failCount = 0;
    const errors: string[] = [];

    for (const productId of idsToDelete) {
      try {
        await deleteProduct(productId);
        successCount++;
      } catch (err: any) {
        failCount++;
        const product = products.find(p => p.id === productId);
        const productName = product ? product.name : `Product ${productId}`;
        errors.push(productName);
        console.error(`Error deleting product ${productId}:`, err);
      }
    }

    // Update local state - remove deleted products
    setProducts(products.filter(p => !selectedProductIds.has(p.id)));

    // Clear selections
    setSelectedProductIds(new Set());
    setLastSelectedIndex(null);
    setBulkDeleteModal({ show: false, count: 0 });
    setBulkOperationLoading(false);

    // Show success/error message
    if (failCount === 0) {
      setSuccess(`Successfully deleted ${successCount} product${successCount > 1 ? 's' : ''}`);
    } else {
      setError(`Deleted ${successCount} product${successCount > 1 ? 's' : ''}, failed to delete ${failCount}: ${errors.join(', ')}`);
    }
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 5000);
  };

  const handleBulkMarkAsUnmapped = async () => {
    if (selectedProductIds.size === 0) return;

    setBulkOperationLoading(true);
    setError('');
    setSuccess('');

    const idsToUpdate = Array.from(selectedProductIds);
    let successCount = 0;
    let failCount = 0;
    const errors: string[] = [];

    for (const productId of idsToUpdate) {
      try {
        await updateProductMapping(productId, {
          local_product_id: null,
          is_ignored: false
        });
        successCount++;
      } catch (err: any) {
        failCount++;
        const product = products.find(p => p.id === productId);
        const productName = product ? product.name : `Product ${productId}`;
        errors.push(productName);
        console.error(`Error marking product ${productId} as unmapped:`, err);
      }
    }

    // Update local state
    setProducts(products.map(p => 
      selectedProductIds.has(p.id)
        ? { 
            ...p, 
            local_product_id: null,
            local_product_name: null,
            mapping_manual: false,
            is_ignored: false
          }
        : p
    ));

    // Clear selections
    setSelectedProductIds(new Set());
    setLastSelectedIndex(null);
    setBulkOperationLoading(false);

    // Show success/error message
    if (failCount === 0) {
      setSuccess(`Successfully marked ${successCount} product${successCount > 1 ? 's' : ''} as unmapped`);
    } else {
      setError(`Marked ${successCount} product${successCount > 1 ? 's' : ''} as unmapped, failed on ${failCount}: ${errors.join(', ')}`);
    }
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 5000);
  };

  const handleBulkMarkAsIgnored = async () => {
    if (selectedProductIds.size === 0) return;

    setBulkOperationLoading(true);
    setError('');
    setSuccess('');

    const idsToUpdate = Array.from(selectedProductIds);
    let successCount = 0;
    let failCount = 0;
    const errors: string[] = [];

    for (const productId of idsToUpdate) {
      try {
        await updateProductMapping(productId, {
          local_product_id: null,
          is_ignored: true
        });
        successCount++;
      } catch (err: any) {
        failCount++;
        const product = products.find(p => p.id === productId);
        const productName = product ? product.name : `Product ${productId}`;
        errors.push(productName);
        console.error(`Error marking product ${productId} as ignored:`, err);
      }
    }

    // Update local state
    setProducts(products.map(p => 
      selectedProductIds.has(p.id)
        ? { 
            ...p, 
            local_product_id: null,
            local_product_name: null,
            is_ignored: true
          }
        : p
    ));

    // Clear selections
    setSelectedProductIds(new Set());
    setLastSelectedIndex(null);
    setBulkOperationLoading(false);

    // Show success/error message
    if (failCount === 0) {
      setSuccess(`Successfully marked ${successCount} product${successCount > 1 ? 's' : ''} as ignored`);
    } else {
      setError(`Marked ${successCount} product${successCount > 1 ? 's' : ''} as ignored, failed on ${failCount}: ${errors.join(', ')}`);
    }
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 5000);
  };

  const getProductStatus = (product: B2BProduct): ProductStatus => {
    if (product.is_ignored) return 'ignored';
    if (product.local_product_id) return 'linked';
    return 'unmapped';
  };

  const getStatusIcon = (product: B2BProduct) => {
    if (product.is_ignored) {
      return <Ban className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0 mt-0.5" />;
    }
    if (product.local_product_id) {
      return <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />;
    }
    // All unmapped products (not linked and not ignored) get warning icon
    if (!product.local_product_id && !product.is_ignored) {
      return <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />;
    }
    return null;
  };

  const getStatusBadge = (status: ProductStatus) => {
    const statusConfig = {
      linked: {
        label: 'Linked',
        className: 'bg-green-100 text-green-800 border-green-200'
      },
      unmapped: {
        label: 'Unmapped',
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      },
      ignored: {
        label: 'Ignored',
        className: 'bg-gray-100 text-gray-800 border-gray-200'
      }
    };

    const config = statusConfig[status];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.className}`}>
        {config.label}
      </span>
    );
  };

  // Filter products based on search term and filter
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchTerm === '' || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.local_product_name && product.local_product_name.toLowerCase().includes(searchTerm.toLowerCase()));

    if (!matchesSearch) return false;

    switch (filter) {
      case 'unmapped':
        return !product.local_product_id && !product.is_ignored;
      case 'has_serial':
        return product.has_serial_number;
      case 'all':
      default:
        return true;
    }
  });

  // Group products by status
  const groupedProducts = filteredProducts.reduce<Record<ProductStatus, B2BProduct[]>>((acc, product) => {
    const status = getProductStatus(product);
    if (!acc[status]) acc[status] = [];
    acc[status].push(product);
    return acc;
  }, { unmapped: [], linked: [], ignored: [] });

  // Status order for rendering
  const statusOrder: ProductStatus[] = ['unmapped', 'linked', 'ignored'];

  // Debug logging
  console.log('Filtered products:', filteredProducts.length);
  console.log('Grouped products:', {
    unmapped: groupedProducts.unmapped.length,
    linked: groupedProducts.linked.length,
    ignored: groupedProducts.ignored.length
  });

  const unmappedWithSerialCount = products.filter(
    p => p.has_serial_number && !p.local_product_id && !p.is_ignored
  ).length;

  // Calculate select all state
  const allSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedProductIds.has(p.id));
  const someSelected = filteredProducts.some(p => selectedProductIds.has(p.id));
  const isIndeterminate = someSelected && !allSelected;

  // Mobile Card Component
  const ProductCard = ({ product }: { product: B2BProduct }) => {
    const status = getProductStatus(product);
    
    return (
      <div 
        className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => setLinkModal({ show: true, product })}
      >
        {/* Header: Status Badge + Action Button */}
        <div className="flex justify-between items-start mb-3">
          {getStatusBadge(status)}
          <IconButton
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setLinkModal({ show: true, product });
            }}
            icon={Edit}
            title={product.local_product_id ? 'Edit' : product.is_ignored ? 'Edit' : 'Link'}
          />
        </div>

        {/* Product Info */}
        <div className="space-y-2">
          <div className="flex items-start">
            {getStatusIcon(product)}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900">
                {product.name}
                {product.local_product_id && (
                  <span className="text-gray-600 font-normal"> → {product.local_product_name}</span>
                )}
                {!product.local_product_id && !product.is_ignored && (
                  <span className="text-gray-500 font-normal italic"> (unmapped)</span>
                )}
                {product.is_ignored && (
                  <span className="text-gray-500 font-normal italic"> (ignored)</span>
                )}
              </div>
              {product.sku && (
                <div className="text-xs text-gray-500 mt-0.5">SKU: {product.sku}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Note: Keep header visible even when loading; show skeleton below

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title="Product Mapping"
        subtitle="Map B2B products to local device products"
        onBack={() => navigate(-1)}
        reserveWidthPx={150}
        actions={
          <Button
            variant="primary"
            onClick={handleSyncProducts}
            disabled={syncingProducts}
            icon={RefreshCw}
          >
            Sync Products
          </Button>
        }
      />

      <SyncProgressBar 
        isSyncing={syncingProducts} 
        message="Syncing products from B2B..." 
        progress={
          syncingProducts && syncStatus?.products?.metadata?.current && syncStatus?.products?.metadata?.total
            ? {
                current: syncStatus.products.metadata.current,
                total: syncStatus.products.metadata.total
              }
            : undefined
        }
      />
      <SyncStatusIndicator syncStatus={syncStatus?.products} />

      {loading && (
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded mb-4" />
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded" />
            <div className="h-20 bg-gray-200 rounded" />
            <div className="h-20 bg-gray-200 rounded" />
            <div className="h-20 bg-gray-200 rounded" />
            <div className="h-20 bg-gray-200 rounded" />
          </div>
        </div>
      )}

      {/* Warning for unmapped products */}
      {!loading && unmappedWithSerialCount > 0 && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-4 flex items-start">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">
              {unmappedWithSerialCount} product{unmappedWithSerialCount > 1 ? 's' : ''} requiring attention
            </h3>
            <p className="text-sm text-yellow-700 mt-1">
              These B2B products require serial numbers but are not mapped to local products.
              Please map them to enable order serial number assignment.
            </p>
          </div>
        </div>
      )}

      {/* Search and Filter Bar */}
      {!loading && (
      <div className="mb-4">
        <SearchFilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          onSearchSubmit={() => {}}
          searchPlaceholder="Search by product name, SKU, or linked product..."
          filters={[
            {
              key: 'filter',
              value: filter,
              placeholder: 'All Products',
              options: [
                { value: 'all', label: 'All Products' },
                { value: 'unmapped', label: 'Unmapped Only' },
                { value: 'has_serial', label: 'Has Serial Number' }
              ]
            }
          ] as any}
              onFilterChange={(key, value) => {
                if (key === 'filter') {
                  setFilter(value as ProductFilter);
                }
              }}
              onSearch={() => {}}
        />
      </div>
      )}

      {/* Success/Error Messages */}
      {error && <AlertMessage type="error" message={error} title="" onDismiss={() => setError('')} dismissible>{''}</AlertMessage>}
      {success && <AlertMessage type="success" message={success} title="" onDismiss={() => setSuccess('')} dismissible>{''}</AlertMessage>}

      {/* Bulk Actions Bar */}
      {!loading && isDesktop && selectedProductIds.size > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-blue-900">
              {selectedProductIds.size} product{selectedProductIds.size > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                const selectedCount = selectedProductIds.size;
                setBulkDeleteModal({ show: true, count: selectedCount });
              }}
              disabled={bulkOperationLoading || selectedProductIds.size === 0}
              icon={undefined}
            >
              Delete
            </Button>
            <Button
              variant="secondary"
              onClick={async () => {
                await handleBulkMarkAsUnmapped();
              }}
              disabled={bulkOperationLoading || selectedProductIds.size === 0}
              icon={undefined}
            >
              Mark as Unmapped
            </Button>
            <Button
              variant="secondary"
              onClick={async () => {
                await handleBulkMarkAsIgnored();
              }}
              disabled={bulkOperationLoading || selectedProductIds.size === 0}
              icon={undefined}
            >
              Mark as Ignored
            </Button>
          </div>
        </div>
      )}

      {/* Desktop/Tablet Table View */}
      {!loading && isDesktop && (
      <div className="hidden md:block">
        <DataTable
          emptyState={filteredProducts.length === 0 ? (
            <EmptyState 
              message={
                searchTerm 
                  ? 'No products match your search'
                  : filter === 'unmapped' 
                    ? 'All products are mapped!' 
                    : 'No products found. Click "Sync Products" to import from B2B.'
              }
              action={!searchTerm && filter === 'all'}
              actionText={!searchTerm && filter === 'all' ? 'Sync Products' : ''}
              onActionClick={!searchTerm && filter === 'all' ? handleSyncProducts : () => {}}
            />
          ) : null}
        >
          <colgroup>
            <col style={{ width: '50px' }} />
            <col />
            <col style={{ width: '100px' }} />
          </colgroup>
          <TableHeader>
            <tr>
              <TableHeaderCell responsive={undefined} align="center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = isIndeterminate;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out cursor-pointer"
                  title="Select all products"
                />
              </TableHeaderCell>
              <TableHeaderCell responsive={undefined}>B2B Product</TableHeaderCell>
              <TableHeaderCell responsive={undefined} align="center">Action</TableHeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {statusOrder.map((status) => {
              const productsInGroup = groupedProducts[status];
              if (productsInGroup.length === 0) return null;

              return (
                <React.Fragment key={status}>
                  {productsInGroup.map((product) => {
                    const index = filteredProducts.findIndex(p => p.id === product.id);
                    const isSelected = selectedProductIds.has(product.id);
                    
                    return (
                      <TableRow 
                        key={product.id}
                        clickable={true}
                        onClick={() => setLinkModal({ show: true, product })} 
                        href={undefined} 
                        onKeyDown={() => {}}
                      >
                        <TableCell responsive={undefined} align="center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleProductSelect(product.id, index, e)}
                            onClick={(e) => e.stopPropagation()}
                            className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out cursor-pointer"
                            title="Select product"
                          />
                        </TableCell>
                        <TableCell responsive={undefined}>
                          <div className="flex items-start max-w-full gap-2">
                            <div className="mt-0.5">
                              {getStatusBadge(getProductStatus(product))}
                            </div>
                            {getStatusIcon(product)}
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <div className="text-sm font-medium text-gray-900 break-words whitespace-normal leading-tight">
                                {product.name}
                                {product.local_product_id && (
                                  <span className="text-gray-600 font-normal"> → {product.local_product_name}</span>
                                )}
                                {!product.local_product_id && !product.is_ignored && (
                                  <span className="text-gray-500 font-normal italic"> (unmapped)</span>
                                )}
                                {product.is_ignored && (
                                  <span className="text-gray-500 font-normal italic"> (ignored)</span>
                                )}
                              </div>
                              {product.sku && (
                                <div className="text-xs text-gray-500 mt-0.5 truncate" title={product.sku}>SKU: {product.sku}</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell responsive={undefined} align="center">
                          <IconButton
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLinkModal({ show: true, product });
                            }}
                            icon={Edit}
                            title={product.local_product_id ? 'Edit' : product.is_ignored ? 'Edit' : 'Link'}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </DataTable>
      </div>
      )}

      {/* Mobile Card View */}
      {!loading && !isDesktop && (
      <div className="block md:hidden">
        {filteredProducts.length === 0 ? (
          <EmptyState 
            message={
              searchTerm 
                ? 'No products match your search'
                : filter === 'unmapped' 
                  ? 'All products are mapped!' 
                  : 'No products found. Click "Sync Products" to import from B2B.'
            }
            action={!searchTerm && filter === 'all'}
            actionText={!searchTerm && filter === 'all' ? 'Sync Products' : ''}
            onActionClick={!searchTerm && filter === 'all' ? handleSyncProducts : () => {}}
          />
        ) : (
          <div className="space-y-6">
            {statusOrder.map((status) => {
              const productsInGroup = groupedProducts[status];
              if (productsInGroup.length === 0) return null;

              return (
                <section key={status} className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                    {getStatusBadge(status)}
                    <span className="text-gray-500">({productsInGroup.length})</span>
                  </h3>
                  <div className="space-y-3">
                    {productsInGroup.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Link/Edit Product Modal */}
      {linkModal.show && linkModal.product && (
        <Modal
          isOpen={linkModal.show}
          onClose={() => {
            setLinkModal({ show: false, product: null });
          }}
          title={`Manage: ${linkModal.product.name}`}
          size="lg"
        >
          <div className="space-y-6">
            {/* Product Info */}
            <div className="bg-gray-50 p-4 rounded-md space-y-2">
              <div>
                <span className="text-xs text-gray-500">SKU:</span>
                <p className="text-sm font-medium">{linkModal.product.sku || 'N/A'}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">B2B Product:</span>
                <p className="text-sm font-medium">{linkModal.product.name}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Requires Serial Number:</span>
                <p className="text-sm font-medium">
                  {linkModal.product.has_serial_number ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Current Status:</span>
                <div className="mt-1">{getStatusBadge(getProductStatus(linkModal.product))}</div>
              </div>
              {linkModal.product.local_product_id && (
                <div>
                  <span className="text-xs text-gray-500">Linked To:</span>
                  <p className="text-sm font-medium">{linkModal.product.local_product_name}</p>
                </div>
              )}
            </div>

            {/* Link Product Section */}
            {!linkModal.product.is_ignored && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  {linkModal.product.local_product_id ? 'Change Link' : 'Link to Local Product'}
                </label>
                <ProductAutocomplete
                  products={localProducts}
                  onSelect={(localProduct) => handleLinkProduct(linkModal.product!, localProduct)}
                  placeholder="Search for a product..."
                  disabled={syncingProducts}
                />
                <p className="text-xs text-gray-500">
                  Search and select a product from the device database to link to this B2B product.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t">
              <div className="flex gap-2">
                {linkModal.product.local_product_id && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setLinkModal({ show: false, product: null });
                      setUnlinkConfirmModal({ show: true, product: linkModal.product });
                    }}
                    icon={X}
                  >
                    Unlink
                  </Button>
                )}
                {!linkModal.product.is_ignored && (
                  <Button
                    variant="secondary"
                    onClick={() => handleMarkAsIgnored(linkModal.product!)}
                    icon={undefined}
                  >
                    Mark as Ignored
                  </Button>
                )}
                {linkModal.product.is_ignored && (
                  <Button
                    variant="secondary"
                    onClick={() => handleUnlinkProduct(linkModal.product!)}
                    icon={undefined}
                  >
                    Remove Ignore Status
                  </Button>
                )}
              </div>
              <Button
                variant="secondary"
                onClick={() => {
                  setLinkModal({ show: false, product: null });
                }}
                icon={undefined}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Unlink Confirmation Modal */}
      {unlinkConfirmModal.show && unlinkConfirmModal.product && (
        <Modal
          isOpen={unlinkConfirmModal.show}
          onClose={() => setUnlinkConfirmModal({ show: false, product: null })}
          title="Confirm Unlink"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to unlink this product mapping?
            </p>
            <div className="bg-gray-50 p-4 rounded-md space-y-2">
              <div>
                <span className="text-xs text-gray-500">B2B Product:</span>
                <p className="font-medium">{unlinkConfirmModal.product.name}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Linked to:</span>
                <p className="font-medium">{unlinkConfirmModal.product.local_product_name}</p>
              </div>
            </div>
            <p className="text-xs text-gray-600">
              This will remove the connection between these products and mark it as unmapped.
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setUnlinkConfirmModal({ show: false, product: null })}
                icon={undefined}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => handleUnlinkProduct(unlinkConfirmModal.product!)}
                icon={undefined}
              >
                Unlink
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteModal.show && (
        <Modal
          isOpen={bulkDeleteModal.show}
          onClose={() => setBulkDeleteModal({ show: false, count: 0 })}
          title="Confirm Bulk Delete"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to delete {bulkDeleteModal.count} product{bulkDeleteModal.count > 1 ? 's' : ''}?
            </p>
            <p className="text-xs text-gray-600">
              This will permanently remove the selected products from the database. They will reappear the next time you sync products from B2B.
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setBulkDeleteModal({ show: false, count: 0 })}
                disabled={bulkOperationLoading}
                icon={undefined}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleBulkDelete}
                disabled={bulkOperationLoading}
                icon={undefined}
              >
                {bulkOperationLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OrderSettings;

